import type { FileEntryCondition, WorkflowStep } from '@/api/workflows';

/**
 * Human-readable summaries of visibility conditions, shared by the read-only
 * review surfaces (WorkflowReviewBody's condition peek + StepConfigDrawer's
 * per-field / per-section condition display). Extracted so there's a single
 * summariser — field labels resolve off the workflow's step list so a rule
 * reads `<authored label> equals "yes"` instead of a raw guid.
 */

/** Operator → human phrasing. `?? c.operator` fallback in describeCondition
 *  handles anything new the BE ships before this table gets updated. */
export const OPERATOR_TEXT: Record<string, string> = {
  equals: 'equals',
  not_equals: 'is not',
  is_true: 'is true',
  is_false: 'is false',
  contains: 'contains',
  not_contains: 'does not contain',
  age_years_lt: 'age <',
  age_years_ge: 'age ≥',
  age_years_between: 'age',
  count_eq: 'instance count =',
  count_ge: 'instance count ≥',
};

/** The one operator carrying a second bound on `valueMax` — see `evaluateConditions.ts`. */
const AGE_RANGE_OPERATOR = 'age_years_between';

/** Walk every step's field tree looking for a matching id. Also accepts a
 *  section id (count operators point at sections not fields — the sectionId
 *  case surfaces the section title). Returns a friendly fallback when nothing
 *  matches. */
export function resolveFieldLabel(id: string, steps: WorkflowStep[], isFr: boolean): string {
  for (const step of steps ?? []) {
    for (const section of step.sections ?? []) {
      if (section.id === id) {
        return (isFr ? section.titleFr : section.titleEn) || section.titleEn || 'section';
      }
      for (const row of section.rows ?? []) {
        for (const col of row.columns ?? []) {
          if (col.field?.id === id) {
            return (isFr ? col.field.labelFr : col.field.labelEn) || col.field.labelEn || 'field';
          }
        }
      }
    }
  }
  return 'field';
}

/**
 * The prefix naming which instances of a repeatable section a condition reads.
 *
 * An index renders 1-BASED ("instance 2" for `'1'`), matching every other place the
 * originator and advisor see an instance numbered — the section's own `{n}` instance label
 * counts from 1, and a summary that disagreed with the form would be worse than silence.
 * The section's authored label isn't reachable here (this module resolves ids, not
 * sections), so the wording stays generic rather than guessing at it.
 */
function describeInstanceScope(scope: FileEntryCondition['instanceScope']): string {
  if (scope === 'all') return 'every ';
  if (scope === 'any') return 'any ';
  if (typeof scope === 'string' && /^\d+$/.test(scope))
    return `instance ${Number.parseInt(scope, 10) + 1} of `;
  return '';
}

/**
 * Render a single condition as `<field label> <operator> <value>`. Falls back
 * to the raw operator + value when the field id can't be resolved.
 */
export function describeCondition(
  c: FileEntryCondition,
  steps: WorkflowStep[],
  isFr: boolean,
): string {
  const fieldLabel = resolveFieldLabel(c.fieldId, steps, isFr);
  const opText = OPERATOR_TEXT[c.operator] ?? c.operator;
  // A range carries both bounds, min inclusive and max exclusive. Rendering `value`
  // alone would read as an open-ended "age 6" and silently drop the upper bound — the
  // half of the rule the operator exists to carry, and the half an approver is most
  // likely to be checking.
  const valueText =
    c.operator === AGE_RANGE_OPERATOR
      ? `${c.value} to under ${c.valueMax ?? '?'}`
      : c.value
        ? `"${c.value}"`
        : '';
  // A scoped source lives inside a repeatable section, so the rule reads over instances
  // rather than over one answer — say which, or "Beneficiary Province equals BC" hides
  // whether one or all beneficiaries must match, and an INDEXED rule hides which instance
  // entirely ("Accountholder 1 is the caregiver" and "Accountholder 2 is" would read
  // identically — the very rows the index exists to tell apart).
  const quantifier = describeInstanceScope(c.instanceScope);
  return `${quantifier}${fieldLabel} ${opText} ${valueText}`.trim();
}

export type ConditionSummaryEntry = { scope: string; summary: string };

/**
 * Walk a step's own conditions + every section inside it, and summarise each
 * rule as `{ scope, summary }` — the condition-peek Popover renders these
 * bullet-by-bullet. Scope names WHY the rule is gating this step ("Step gate"
 * vs the specific section title); summary spells the rule out with the source
 * field's authored label.
 */
export function collectStepConditions(
  step: WorkflowStep,
  steps: WorkflowStep[],
  isFr: boolean,
): ConditionSummaryEntry[] {
  const out: ConditionSummaryEntry[] = [];
  const stepScope = 'Step gate';
  for (const c of step.conditions ?? []) {
    out.push({ scope: stepScope, summary: describeCondition(c, steps, isFr) });
  }
  // "Disable if" rules get their own scope label rather than being folded in with the
  // visibility ones: they have the opposite polarity (a MATCH disables) and they do NOT
  // drop the answers, so an approver reading the peek must be able to tell them apart.
  for (const c of step.disableConditions ?? []) {
    out.push({ scope: 'Step disable gate', summary: describeCondition(c, steps, isFr) });
  }
  for (const section of step.sections ?? []) {
    const sectionTitle =
      (isFr ? section.titleFr : section.titleEn) || section.titleEn || section.titleFr || 'Section';
    for (const c of section.conditions ?? []) {
      out.push({
        scope: `Section «${sectionTitle}»`,
        summary: describeCondition(c, steps, isFr),
      });
    }
    for (const c of section.disableConditions ?? []) {
      out.push({
        scope: `Section «${sectionTitle}» disable gate`,
        summary: describeCondition(c, steps, isFr),
      });
    }
  }
  return out;
}
